import {usePageSeo} from '@/lib/seo';
import ResultOrder from '@/components/ResultOrder';
import ResultView,{useResultView} from '@/components/ResultView';
import {visitSeed} from '@/lib/visitSeed';
import {orderRecords} from '@/lib/resultRecords';
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { FarmCard } from "@/components/FarmCard";
import { DataMessage } from "@/components/Editorial";
import { SciName } from "@/components/SciName";

export default function CropDetail() {
  const { slug } = useParams();
  const { t, lang, tv } = useLang();
  const [view,setView]=useResultView();
  const [order,setOrder]=useState("random"),[page,setPage]=useState(1);
  const [seed]=useState(visitSeed);
  useEffect(()=>setPage(1),[slug,order]);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController(); setData(null); setError("");
    api.get(`/crops/${encodeURIComponent(slug)}`, { signal: controller.signal }).then((r) => {
      if (controller.signal.aborted) return;
      if (!r.data?.category && !r.data?.farms?.length) setError("missing");
      else setData(r.data);
    }).catch(e => { if (!controller.signal.aborted) setError(e.response?.status === 404 ? "missing" : "load"); });
    return () => controller.abort();
  }, [slug, attempt]);

  const seoName = data?.category?.labels?.[lang] || tv(data?.category?.name || '') || '';
  usePageSeo({title:seoName,description:seoName ? (lang === 'it' ? `Scopri ${seoName}: informazioni sulla coltura e soggetti che dichiarano di coltivarla nell’atlante PlantAdopting.` : `Explore ${seoName}: crop information and subjects declaring it in the PlantAdopting atlas.`) : '', image:data?.category?.image, noindex:!data || !!error});
  if (error || !data) return <div className="grain-bg min-h-[60vh] p-8 sm:p-14" data-testid="crop-detail-page"><div className="max-w-4xl mx-auto"><h1 className="font-serif text-3xl mb-6">{error === "missing" ? t("content.cropMissing") : t("pages.cropsTitle")}</h1><DataMessage error={!!error} onRetry={error === "load" ? () => setAttempt(a => a + 1) : undefined} retryLabel={t("content.retry")}>{error === "load" ? t("content.cropError") : error === "missing" ? (lang === "it" ? "Scegli una categoria disponibile nel catalogo." : "Choose an available category from the catalog.") : t("content.loading")}</DataMessage><Link to="/crops" className="inline-block mt-6 underline text-[#557448]">{t("nav.crops")}</Link></div></div>;

  const cat = data?.category;
  const farms = data?.farms || [];
  const ordered=orderRecords(farms,order,seed,lang);
  const pages=Math.max(1,Math.ceil(farms.length/24)),currentPage=Math.min(page,pages);
  const pageItems=ordered.slice((currentPage-1)*24,currentPage*24);
  const rawName = cat?.name || (slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : "");
  const title = cat?.labels?.[lang] || tv(rawName) || rawName;
  const sci = cat?.species;

  return (
    <div data-testid="crop-detail-page">
      <div className="relative h-[34vh]">
        <img src={cat?.image || "https://images.unsplash.com/photo-1722228097356-bd0202d99367?w=1600"} alt={title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2A2521]/85 to-[#2A2521]/25" />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-7">
            <Link to="/crops" className="inline-flex items-center gap-1.5 text-[#E2DACB] font-sans text-sm mb-3 hover:text-[#F8F5EA]">
              <ArrowLeft size={15} /> {t("nav.crops")}
            </Link>
            <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#F8F5EA] capitalize">{title}</h1>
            {sci && <p className="text-[#F3EFE2]/90 font-sans mt-1"><SciName name={sci} /></p>}
          </div>
        </div>
      </div>

      <div className="grain-bg min-h-[40vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <p className="text-sm text-[#6E6258] mb-5">{lang === "it" ? "I risultati mostrano i soggetti che dichiarano questa coltura. Le informazioni delle schede sono dichiarate dai promotori, salvo una specifica indicazione di verifica." : "Results show subjects declaring this crop. Profile information is declared by promoters unless a specific verification is stated."}</p>
          <h2 className="font-serif text-2xl font-semibold text-[#2A2521] mb-6">
            {farms.length} {t("home.farmsCount")}
          </h2>
          <ResultOrder order={order} onOrder={setOrder} page={currentPage} pages={pages} total={farms.length} onPage={setPage} loading={false}/>
          <ResultView value={view} onChange={setView}/>
          {farms.length === 0 ? (
            <p className="font-sans text-[#6E6258]">{t("content.cropEmpty")} <Link className="block mt-3 underline text-[#557448]" to="/atlas">{t("home.viewAllFarms")}</Link></p>
          ) : (
            <div className={view==="compact"?"grid gap-3":"grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"}>
              {pageItems.map((f) => <FarmCard key={f.id} farm={f} compact={view==="compact"} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

